import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './context/ThemeContext';
import { NavigationProvider } from './context/NavigationContext';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import ErrorBoundary from './components/desktop/ErrorBoundary';
import { migrateThemeCaches } from './utils/themeCache';
import LoginOverlay from './components/desktop/LoginOverlay';
import ClaimUsernameModal from './components/desktop/ClaimUsernameModal';

// Migrate theme caches on app startup to clear old/stale caches
migrateThemeCaches();

// Layout
import Layout from './components/desktop/Layout';

// Public Pages
import Login from './pages/Login';
import Register from './pages/Register';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Protected Pages
import Feed from './pages/Feed';
import ProfileRedirect from './pages/ProfileRedirect';
import UserProfile from './pages/UserProfile';
import Friends from './pages/Friends';
import Messages from './pages/Messages';
import MessageThread from './pages/MessageThread';
import Communities from './pages/Communities';
import CommunityDetail from './pages/CommunityDetail';
import Search from './pages/Search';
import Settings from './pages/Settings';
import Ads from './pages/Ads';
import Marketplace from './pages/Marketplace';
import PostView from './pages/PostView';
import ForumPostView from './pages/ForumPostView';
import TagsPage from './pages/TagsPage';
import PostDetail from './pages/PostDetail';
import Moderation from './pages/Moderation';

function GlobalLoginModal() {
  const { showLoginModal, setShowLoginModal } = useAuth();
  return (
    <LoginOverlay
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      onSuccess={() => setShowLoginModal(false)}
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AuthProvider>
            <ErrorBoundary>
              <ThemeProvider>
                <ErrorBoundary>
                  <NavigationProvider>
                    <ErrorBoundary>
                      <SocketProvider>
                        <BrowserRouter
                          future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true,
                          }}
                        >
                          <Routes>
                {/* Redirect root to feed */}
                <Route path="/" element={<Navigate to="/feed" replace />} />

                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                {/* Company redirects new signups back here; AuthContext bootstrap runs the silent refresh and either lands the user on /feed or surfaces the claim-username modal. */}
                <Route path="/post-signup" element={<Navigate to="/feed" replace />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Protected Routes - Wrapped in Layout */}
                <Route element={<Layout />}>
                  <Route path="/feed" element={<Feed />} />

                  {/* User Routes - /user redirects to logged-in user */}
                  <Route path="/user" element={<ProfileRedirect />} />
                  <Route path="/user/:username" element={<UserProfile />} />
                  <Route path="/user/:username/:slug" element={<PostDetail />} />

                  {/* Short User Routes - /u also redirects to logged-in user */}
                  <Route path="/u" element={<ProfileRedirect />} />
                  <Route path="/u/:username" element={<UserProfile />} />
                  <Route path="/u/:username/:slug" element={<PostDetail />} />

                  {/* Legacy redirects from old /profile and /p routes */}
                  <Route path="/profile" element={<Navigate to="/user" replace />} />
                  <Route path="/profile/:username" element={<Navigate to="/user/:username" replace />} />
                  <Route path="/profile/:username/:slug" element={<Navigate to="/user/:username/:slug" replace />} />
                  <Route path="/p" element={<Navigate to="/u" replace />} />
                  <Route path="/p/:username" element={<Navigate to="/u/:username" replace />} />
                  <Route path="/p/:username/:slug" element={<Navigate to="/u/:username/:slug" replace />} />

                  <Route path="/friends" element={<Friends />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/messages/:id" element={<MessageThread />} />
                  <Route path="/community" element={<Communities />} />

                  {/* Community routes - specific routes first */}
                  <Route path="/community/:communitySlug/forum/:postSlug" element={<ForumPostView />} />
                  <Route path="/community/:id" element={<CommunityDetail />} />
                  <Route path="/community/:communitySlug/:postSlug" element={<PostView />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/moderation" element={<Moderation />} />
                  <Route path="/ads" element={<Ads />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/tags/:tag" element={<TagsPage />} />

                </Route>

                {/* Catch all - redirect to feed */}
                <Route path="*" element={<Navigate to="/feed" replace />} />
                          </Routes>

                          <Toaster />
                          <GlobalLoginModal />
                          <ClaimUsernameModal />
                        </BrowserRouter>
                      </SocketProvider>
                    </ErrorBoundary>
                  </NavigationProvider>
                </ErrorBoundary>
              </ThemeProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
